/* eslint-disable react-hooks/exhaustive-deps */
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Subject } from 'rxjs';

import { FormComponent, FormComponentProps } from '..';
import FormFile from '../../../interfaces/file';
import { useService } from '../../../services/services';
import { downloadFile, parseError, uuid } from '../../../services/utils';
import { RootState } from '../../../store/store';
import FormField from '../../common/form-field';
import Spinner from '../../common/spinner';
import css from './file.module.css';
import { SystemConfiguration } from '../../../enums/system-configuration';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import { AnimalEventService } from '../../../services/animal-event.service';

export default function File(
  name: string,
  label: string = null,
  multiple: boolean = true,
  customService?: AnimalEventService,
  column?: boolean,
  transKeyPrefix?: string,
): FormComponent {
  return ({ ...rest }) => (
    <FileComponent
      name={name}
      label={label}
      multiple={multiple}
      customService={customService}
      column={column}
      transKeyPrefix={transKeyPrefix}
      {...rest}
    />
  );
}

export function FileComponent({
  name,
  label,
  parentName,
  multiple,
  readonly,
  disabled,
  required,
  formValues,
  customService,
  column,
  transKeyPrefix,
}: FormComponentProps & {
  name: string;
  label: string;
  multiple: boolean;
  required?: string[];
  formValues?: object;
  customService?: AnimalEventService;
  column?: boolean;
  transKeyPrefix?: string;
}) {
  const { farmId, mode, id } = useParams<{
    farmId: string;
    mode?: string;
    id?: string;
  }>();
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    setValue,
    setError,
    trigger,
  } = useFormContext();
  const { fileService } = useService();
  const { systemPropertyService } = useService();
  const [files, setFiles] = useState<FormFile[]>(null);
  const [invalidFiles, setInvalidFiles] = useState<FormFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number;
  }>({});

  const inputName = parentName ? `${parentName}.${name}` : name;
  const error = parseError(inputName, errors);

  const { uploadFileTypes, uploadFileMaxSize } = useSelector(
    (state: RootState) => ({
      uploadFileTypes:
        state.global.systemConfiguration[
          SystemConfiguration.UPLOAD_FILE_EXTENSIONS
        ].split(';'),
      uploadFileMaxSize:
        parseInt(
          state.global.systemConfiguration[
            SystemConfiguration.UPLOAD_FILE_MAX_SIZE
          ]
        ) *
        1024 *
        1024,
    })
  );

  const [uploadMimeTypes, setUploadMimeTypes] = useState<string[]>(null);

  // Initial files[] setup from formValues
  useEffect(() => {
    if (files !== null) {
      return;
    }

    systemPropertyService
      .getAllowedMimeTypes()
      .subscribe((response: string[]) => {
        setUploadMimeTypes(response);
      });

    const values = inputName
      .split('.')
      .reduce((accumulator, currentValue: any) => {
        return accumulator?.hasOwnProperty(currentValue)
          ? accumulator[currentValue]
          : false;
      }, formValues);

    if (values?.length > 0) {
      if (multiple) {
        setFiles(values);
      } else {
        setFiles([values[0]]);
      }
    } else {
      setFiles([]);
    }
  }, [
    files,
    uploadMimeTypes,
    inputName,
    multiple,
    formValues,
    systemPropertyService,
  ]);

  // Update and validate input value from files[]
  useEffect(() => {
    if (files === null) {
      return;
    }
    setValue(inputName, files);
    trigger(inputName).then((valid) => {
      if (valid && invalidFiles.length > 0) {
        setError(inputName, {
          type: 'manual',
          message: t('form.validate.Size or type error', {
            maxSize: uploadFileMaxSize / 1024 / 1024,
          }),
        });
      }
    });
  }, [
    files,
    inputName,
    setValue,
    setError,
    trigger,
    uploadFileMaxSize,
    invalidFiles.length,
    t,
  ]);

  function selectFiles(event: ChangeEvent<HTMLInputElement>) {
    Array.from(event.target.files).forEach((file) => {
      upload(file);
    });
  }

  function upload(file: File) {
    // Wait for system properties
    if (
      uploadFileMaxSize === null ||
      uploadFileTypes === null ||
      uploadMimeTypes === null
    ) {
      return;
    }

    // Ignore already added files
    if (files.findIndex((f) => f.name === file.name) !== -1) {
      return;
    }

    if (file.size > uploadFileMaxSize || !uploadMimeTypes.includes(file.type)) {
      setInvalidFiles((files) =>
        files.concat({
          id: uuid(),
          name: file.name,
          size: file.size,
        } as FormFile)
      );
      return;
    }

    const progressSubject = new Subject<ProgressEvent>();

    setUploadProgress((state) => {
      state[file.name] = 0;
      return { ...state };
    });
    const service = customService
      ? customService.uploadFile(farmId, file, progressSubject)
      : fileService.store(file, progressSubject);
    service.subscribe({
      next: (file) => {
        setFiles((state) => (multiple ? state.concat(file) : [file]));
      },
      error: (error) => {
        throw error;
      },
    });

    const progressSubjectDone = () => {
      setUploadProgress((state) => {
        delete state[file.name];
        return { ...state };
      });
    };

    progressSubject.subscribe({
      next: (progress) => {
        setUploadProgress((state) => {
          state[file.name] = Math.round(
            (progress.loaded / progress.total) * 100
          );
          return { ...state };
        });
      },
      error: progressSubjectDone,
      complete: progressSubjectDone,
    });
  }

  function destroy(file: FormFile) {
    if (mode === 'edit' && customService) {
      customService.destroyFile(farmId, id, file.id);
    }
    setFiles((files) => files.filter((f) => f.id !== file.id));
    setInvalidFiles((files) => files.filter((f) => f.id !== file.id));
  }

  const download = useCallback(
    (fileId: string, name = 'file.pdf') => {
      const service = customService
        ? customService.download(farmId, id, fileId)
        : fileService.download(fileId);
      service.subscribe((file: Blob) => {
        downloadFile(file, name);
      });
    },
    [fileService, customService]
  );

  function fileList() {
    if (files === null) {
      return;
    }

    return (
      <ul
        className={`${css['files']} ${
          files.length + invalidFiles.length === 0
            ? css['without-files']
            : css['with-files']
        }`}
      >
        {files
          .concat(invalidFiles)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((file, index) => (
            <li key={index} className={css['file-container']}>
              <div
                className={`${css['file']} ${
                  invalidFiles.indexOf(file) !== -1 ? css['error'] : ''
                }`}
              >
                {readonly ? (
                  ''
                ) : (
                  <div className={css['destroy']} onClick={() => destroy(file)}>
                    <FontAwesomeIcon icon={faTimes} />
                  </div>
                )}
                <div
                  className={css['name']}
                  onClick={() => download(file.id, file.name)}
                >
                  {file.name}
                </div>
                <div className={css['size']}>
                  {Math.ceil((file.size / 1024 / 1024) * 100) / 100} MB
                </div>
              </div>
            </li>
          ))}
      </ul>
    );
  }

  if (readonly) {
    return (
      <FormField
        label={label ? t(label) : label === null ? null : t(name)}
        labelFor={inputName}
        required={required?.includes(inputName)}
        error={error ? error.message : null}
        column={column}
      >
        <input type="hidden" name={inputName} {...register(inputName)} />
        {fileList()}
      </FormField>
    );
  } else {
    return (
      <FormField
        label={label ? t(label) : label === null ? null : t(name)}
        labelFor={inputName}
        required={required?.includes(inputName)}
        error={error ? error.message : null}
        column={column}
      >
        <input type="hidden" name={inputName} {...register(inputName)} />
        <label
          className={cn(
            css['input-container'],
            customService && css['input-container_event']
          )}
        >
          <input
            type={'file'}
            multiple={multiple}
            onChange={selectFiles}
            readOnly={readonly}
            disabled={disabled}
          />
          <div>
            <div
              className={customService ? css['upload_event'] : css['upload']}
            >
              <Spinner visible={Object.keys(uploadProgress).length > 0} />
              {transKeyPrefix ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: t(transKeyPrefix?.concat('upload')),
                  }}
                />
              ) : (
                <span>{t('upload')}</span>
              )}
            </div>
          </div>
          {uploadFileTypes === null ||
          uploadFileMaxSize === null ||
          uploadMimeTypes === null ? (
            ''
          ) : (
            <div className={css['help']}>
              <span>
                <Trans
                  t={t}
                  i18nKey={
                    transKeyPrefix
                      ? transKeyPrefix?.concat('Upload restrictions')
                      : 'pages.internal.system-configuration.Upload restrictions'
                  }
                  components={{ 1: <strong /> }}
                  values={{
                    fileTypes: uploadFileTypes.join(', '),
                    maxSize: uploadFileMaxSize / 1024 / 1024,
                  }}
                />
              </span>
            </div>
          )}
        </label>
        {fileList()}
      </FormField>
    );
  }
}
